import React from 'react';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import {
  Box,
  Card,
  LinearProgress,
  Link,
  ListItemText,
  ListItemTextProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import CardContent from '../../../common/card/CardContent';
import CardTitle from '../../../common/card/CardTitle';
import { TaskSummaryItem } from '../../tasks/Task.interface';
import {
  DashboardChartData,
  DashboardItem,
  DashboardItemColumn
} from '../Dashboard.interface';
import DashboardChart from './DashboardChart';
import DashboardItemGraph from './DashboardItemGraph';

const StyledCardContent = styled(CardContent)`
  position: relative;
  height: 360px;
  /* use class for image container to center the image */
  .img-empty-container {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding-bottom: 48px;
  }
  /* use class for images to properly center and adjust its size */
  .img-empty {
    width: 100%;
    padding: 0 8px;
    max-width: 320px;
  }
  .content {
    padding: 8px;
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
  }
`;

const StyledTableRow = styled(TableRow)`
  .MuiTableCell-root {
    padding-top: 2px;
    padding-bottom: 2px;
  }
  .MuiListItemText-primary {
    white-space: normal;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

const StyledListItemText = (props: ListItemTextProps) => (
  <ListItemText
    {...props}
    primaryTypographyProps={{ style: { fontWeight: 'bold' } }}
  />
);

export type DashboardCardOrder = 'title' | 'status';

export interface BaseDashboardCardProps {
  items: DashboardItem[];
  itemList?: TaskSummaryItem[];
  chart?: DashboardChartData;
  useGraph?: boolean;
  useItemGraph?: boolean;
  total?: number;
  isLoading?: boolean;
  title: string;
  icon?: React.ReactNode;
  emptyState?: React.ReactNode;
  page?: number;
  limit?: number;
  orderDirection?: string;
  /** Use table pagination instead of "Load More" button. */
  pagination?: boolean;
  rowsPerPageOptions?: number[];
  rowsPerPage?: number;
  setPage?: (page: number) => void;
  setLimit?: (limit: number) => void;
  setOrder?: (order: DashboardCardOrder) => void;
  isActive?: (order: DashboardCardOrder) => boolean;
  setOrderDirection?: (orderDirection: 'ASC' | 'DESC') => void;
}

const BaseDashboardCard: React.FC<BaseDashboardCardProps> = (
  props: BaseDashboardCardProps
) => {
  const {
    icon,
    items,
    itemList,
    useGraph = true,
    useItemGraph = false,
    total,
    chart,
    isLoading = false,
    emptyState = 'No pending tasks.',
    title,
    page = 0,
    limit = 10,
    orderDirection,
    pagination,
    rowsPerPageOptions = [5, 10, 25],
    rowsPerPage = limit,
    setPage,
    setLimit,
    setOrder,
    isActive,
    setOrderDirection
  } = props;
  const history = useHistory();

  const handleSort = (column: DashboardCardOrder) => {
    setOrder?.(column);
    setOrderDirection?.(orderDirection === 'ASC' ? 'DESC' : 'ASC');
  };

  const getSortDirection = (column: DashboardCardOrder): 'asc' | 'desc' => {
    return isActive?.(column) && orderDirection === 'DESC' ? 'desc' : 'asc';
  };

  const isEmpty = useItemGraph
    ? itemList?.every(item => item.total === 0)
    : chart?.total === 0;

  return (
    <Card>
      <CardTitle>
        {icon}
        {title} {typeof total === 'number' ? `(${total})` : undefined}
      </CardTitle>
      {useGraph && (
        <StyledCardContent>
          {useItemGraph ? (
            <DashboardItemGraph itemList={itemList || []} />
          ) : (
            <DashboardChart data={chart} />
          )}
          {isEmpty && (
            <Box className='content'>
              <Alert severity='success'>You're all caught up!</Alert>
            </Box>
          )}
        </StyledCardContent>
      )}
      {!isEmpty && (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={isActive?.('title')}
                    direction={getSortDirection('title')}
                    onClick={() => handleSort('title')}
                  >
                    <StyledListItemText
                      primary='ID / Title'
                      secondary={'Date Assigned'}
                    />
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={isActive?.('status')}
                    direction={getSortDirection('status')}
                    onClick={() => handleSort('status')}
                  >
                    <StyledListItemText primary='Status' />
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={3} padding='none'>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3}>{emptyState}</TableCell>
                </TableRow>
              )}
              {items.map((item: DashboardItem) => (
                <StyledTableRow
                  key={item.id}
                  hover={!item.disabled && !!(item.to || item.onClick)}
                  onClick={() => {
                    if (item.disabled) {
                      // do nothing if disabled
                    } else if (item.to) {
                      history.push(item.to);
                    } else {
                      item.onClick?.();
                    }
                  }}
                >
                  {item.columns.map(
                    (column: DashboardItemColumn, index: number) => (
                      <ColumnItem
                        column={column}
                        key={`${column.primary}-${index}`}
                      />
                    )
                  )}
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {pagination ? (
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component='div'
          count={total || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, page) => setPage?.(page)}
          onRowsPerPageChange={event => {
            setLimit?.(+event.target.value);
            setPage?.(0);
          }}
        />
      ) : (
        typeof total === 'number' &&
        total > (page + 1) * limit && (
          <Box textAlign='center' padding={2}>
            <Link
              variant='body2'
              component='button'
              onClick={() => setPage?.(page + 1)}
            >
              Load More
            </Link>
          </Box>
        )
      )}
    </Card>
  );
};

export default BaseDashboardCard;

export interface ColumnItemProps {
  column: DashboardItemColumn;
}

export const ColumnItem: React.FC<ColumnItemProps> = (
  props: ColumnItemProps
) => {
  const { column } = props;
  return (
    <TableCell>
      <Tooltip
        title={column.tooltip != null ? column.tooltip : column.primary || ''}
      >
        {/* satisfy `Tooltip` children typings by wrapping `render` */}
        {column.render ? (
          <>{column.render()}</>
        ) : (
          <ListItemText primary={column.primary} secondary={column.secondary} />
        )}
      </Tooltip>
    </TableCell>
  );
};
